'use client';

import {useI18n} from '@udemy-v2/i18n';

import {getAssetBase} from '@/lib/asset-base';

import styles from './landing-banner.module.scss';

const ASSET_BASE = getAssetBase('landing-banner/v2');

/** Placeholder the heading copy is split on so the whole sentence stays one msgid. */
const HIGHLIGHT_PLACEHOLDER = '%(highlight)s';

/**
 * Hero banner for the Instructor Community landing page — Figma node 6553:176779
 * (desktop) and 6553:173733 (mobile), file `mitwWlKOuf1pyo4gUCLEBY`.
 *
 * A full-bleed photo of instructors wearing headsets over a dark band, the page's
 * `h1` with the word "Communities!" carrying the brand gradient, and a supporting
 * paragraph. Presentational and copy-only — no props and no data.
 */
export function LandingBanner() {
    const {gettext} = useI18n();

    // One msgid for the whole sentence so translators control word order around the
    // highlighted word, rather than receiving three disconnected fragments.
    const [beforeHighlight, afterHighlight] = gettext('Join Udemy %(highlight)s').split(
        HIGHLIGHT_PLACEHOLDER,
    );

    return (
        <section className={styles['banner']} data-purpose="landing-banner">
            {/* Full-bleed photo behind the copy. The heading and description carry the
                meaning, so the image is presentational. Sits outside the centred column
                below so it can always reach the viewport edge. */}
            <picture className={styles['art']}>
                <source
                    media="(max-width: 900px)"
                    srcSet={`${ASSET_BASE}/banner-art-mobile.webp`}
                />
                <img src={`${ASSET_BASE}/banner-art-desktop.webp`} alt="" />
            </picture>

            {/* Centred column holding the copy at its design offset. */}
            <div className={styles['inner']}>
                <div className={styles['content']}>
                    <h1 className={styles['title']}>
                        {beforeHighlight}
                        <span className={styles['title-highlight']}>{gettext('Communities!')}</span>
                        {afterHighlight}
                    </h1>
                    <p className={styles['description']}>
                        {gettext(
                            'Join a community led by an instructor you trust, where you can get guidance, put your skills into practice, and learn alongside people working toward similar goals.',
                        )}
                    </p>
                </div>
            </div>
        </section>
    );
}
